import React, { useCallback } from 'react';
import PerformanceMark from '@atlassian/jira-common-performance/src/set-performance-mark';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries';
import { componentWithFF } from '@atlassian/jira-feature-flagging-utils';
import {
	GlobalBottomRightCornerContainer,
	LayoutRemoteStateUpdater,
} from '@atlassian/jira-layout-controller';
import { useHorizontalNavComponent } from '@atlassian/jira-navigation-apps-horizontal-nav-selector';
import { PageLayout, Content, LeftPanel, Main } from '@atlassian/jira-navigation-system';
import {
	GLOBAL_CONTAINERS_MARK,
	GLOBAL_CONTAINERS_START_MARK,
	GLOBAL_CONTAINERS_END_MARK,
} from '@atlassian/jira-page-container-constants';
import { ContextualAnalyticsData, SCREEN } from '@atlassian/jira-product-analytics-bridge';
import { sharedLayout } from '@atlassian/jira-shared-layout';
import { setInitialPageLoadTimingFromPerformanceMarks } from '@atlassian/jira-spa-performance-breakdown';
import { Banner } from '../banner';
import { GlobalComponents } from '../global-components';
import { GlobalContainers } from '../global-containers';
import { HorizontalNavWrapper } from '../horizontal-nav';
import { RightSidebar } from '../right-sidebar';
import { Sidebar } from '../sidebar';
import { TopNav } from '../topnav';
import { FullHeight } from './styled';
import { ThemeSetter } from './theme-setter';
import type { PageContainerProps } from './types';

/* IMPORTANT:
 * When making any changes to the OLD component, please ensure that you also make the same changes to the
 * NEW component. The feature flag used is part of the Navigation Refresh work and we need to ensure that
 * we do not introduce tech debt for the roll-out by only updating the current component that is due to be
 * removed.
 *
 * If you have any concerns or questions please reach out to the #jira-navigation channel. Thanks!
 */

const PageContainerNew = ({
	id,
	locale,
	baseUrl,
	children,
	messages,
	Router,
	routerLinkComponent,
}: PageContainerProps) => {
	const HorizontalNavComponent = useHorizontalNavComponent();
	const pageContainerPerformanceTiming = useCallback(() => {
		setInitialPageLoadTimingFromPerformanceMarks(
			GLOBAL_CONTAINERS_MARK,
			GLOBAL_CONTAINERS_START_MARK,
			GLOBAL_CONTAINERS_END_MARK,
			true,
			false,
		);
	}, []);

	const shouldFixMain = !!HorizontalNavComponent;
	let contentChildren = children;

	if (HorizontalNavComponent != null) {
		contentChildren = (
			<HorizontalNavWrapper navComponent={HorizontalNavComponent}>{children}</HorizontalNavWrapper>
		);
	}

	return (
		<>
			<PerformanceMark metricKey={GLOBAL_CONTAINERS_START_MARK} />
			<ThemeSetter />
			<GlobalContainers
				id={id}
				locale={locale}
				baseUrl={baseUrl}
				messages={messages}
				Router={Router}
				routerLinkComponent={routerLinkComponent}
			>
				<PerformanceMark
					metricKey={GLOBAL_CONTAINERS_END_MARK}
					onSet={pageContainerPerformanceTiming}
				/>
				<ContextualAnalyticsData sourceType={SCREEN} sourceName="navigationNext">
					<PageLayout skipLinksLabel="">
						<JSErrorBoundary id="banner" packageName="jiraPageContainerV2" fallback="unmount">
							<Banner />
						</JSErrorBoundary>
						<JSErrorBoundary id="top-nav" packageName="jiraPageContainerV2" fallback="unmount">
							<TopNav />
						</JSErrorBoundary>
						<JSErrorBoundary id="sidebar" packageName="jiraPageContainerV2" fallback="unmount">
							<Sidebar />
						</JSErrorBoundary>
						<Main isFixed={shouldFixMain}>{contentChildren}</Main>
						<JSErrorBoundary
							id="right-sidebar"
							packageName="jiraPageContainerV2"
							fallback="unmount"
						>
							<RightSidebar />
						</JSErrorBoundary>
						<JSErrorBoundary
							packageName="jiraPageContainerV2"
							id="layout-remote-state-updater"
							fallback="unmount"
						>
							<LayoutRemoteStateUpdater />
						</JSErrorBoundary>
					</PageLayout>
					<GlobalComponents sharedGlobalComponents={sharedLayout.globalComponents} />
					<GlobalBottomRightCornerContainer />
				</ContextualAnalyticsData>
			</GlobalContainers>
		</>
	);
};

/**
 * Serves as a comprehensive layout manager, orchestrating the assembly of global containers,
 * navigation components, and performance metrics into a cohesive page structure. It enhances user
 * interface by conditionally incorporating additional UI elements and ensures a uniform
 * appearance and behavior across pages through global styling. Moreover, it facilitates
 * performance optimization and analytics integration for improved user experience and
 * insights.
 */
const PageContainerOld = ({
	id,
	locale,
	baseUrl,
	children,
	messages,
	Router,
	routerLinkComponent,
}: PageContainerProps) => {
	const HorizontalNavComponent = useHorizontalNavComponent();
	const pageContainerPerformanceTiming = useCallback(() => {
		setInitialPageLoadTimingFromPerformanceMarks(
			GLOBAL_CONTAINERS_MARK,
			GLOBAL_CONTAINERS_START_MARK,
			GLOBAL_CONTAINERS_END_MARK,
			true,
			false,
		);
	}, []);

	let contentChildren = children;

	if (HorizontalNavComponent != null) {
		contentChildren = (
			<HorizontalNavWrapper navComponent={HorizontalNavComponent}>{children}</HorizontalNavWrapper>
		);
	}

	const panelContent = (
		<>
			<FullHeight>
				<PageLayout skipLinksLabel="">
					<JSErrorBoundary id="banner" packageName="page-container" fallback="unmount">
						<Banner />
					</JSErrorBoundary>
					<JSErrorBoundary id="top-nav" packageName="page-container" fallback="unmount">
						<TopNav />
					</JSErrorBoundary>
					<LeftPanel width={0}>
						<></>
					</LeftPanel>
					<Content>
						<JSErrorBoundary id="sidebar" packageName="page-container" fallback="unmount">
							<Sidebar />
						</JSErrorBoundary>
						{contentChildren}
						<JSErrorBoundary id="right-sidebar" packageName="page-container" fallback="unmount">
							<RightSidebar />
						</JSErrorBoundary>
					</Content>
					<JSErrorBoundary
						packageName="page-container"
						id="layout-remote-state-updater"
						fallback="unmount"
					>
						<LayoutRemoteStateUpdater />
					</JSErrorBoundary>
				</PageLayout>
			</FullHeight>
			<GlobalComponents sharedGlobalComponents={sharedLayout.globalComponents} />
			<GlobalBottomRightCornerContainer />
		</>
	);

	return (
		<>
			<PerformanceMark metricKey={GLOBAL_CONTAINERS_START_MARK} />
			<ThemeSetter />
			<GlobalContainers
				id={id}
				locale={locale}
				baseUrl={baseUrl}
				messages={messages}
				Router={Router}
				routerLinkComponent={routerLinkComponent}
			>
				<PerformanceMark
					metricKey={GLOBAL_CONTAINERS_END_MARK}
					onSet={pageContainerPerformanceTiming}
				/>
				<ContextualAnalyticsData sourceType={SCREEN} sourceName="navigationNext">
					{panelContent}
				</ContextualAnalyticsData>
			</GlobalContainers>
		</>
	);
};

export const PageContainer = componentWithFF(
	'platform.atlassian.griffin-new-page-layout',
	PageContainerNew,
	PageContainerOld,
);
